import { d as __rest, _ as __assign } from './_tslib-3301712a.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useEffect, useState, memo, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import Loading, { LoadingColor, LoadingScale } from 'bloko/blocks/loading';
import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';

var VIEWER_NODE_ID = 'chatikIntegration_viewer';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$2 = ".icon-button {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  overflow: hidden;\n}\n.icon-button_small {\n  width: 40px;\n  height: 40px;\n}\n.icon-button_medium {\n  width: 48px;\n  height: 48px;\n}\n.icon-button_default svg {\n  color: #000;\n  stroke: #000;\n}\n.icon-button_default svg:hover {\n  background-color: #f4f6fb;\n}\n.icon-button_contrast svg {\n  color: #FFF;\n  stroke: #FFF;\n}\n:global(.focus-visible) .icon-button {\n  outline: 4px solid #0070ff7a;\n}\n.icon-button-element {\n  display: flex;\n  background: transparent;\n  border: none;\n}\n.icon-button-element:active {\n  transform: scale(0.96);\n  transition-duration: 100ms;\n  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);\n}\n.icon-button-element:focus {\n  outline: none;\n}\n";
styleInject(css_248z$2);

var IconButton = function (_a) {
    var _b = _a.Element, Element = _b === void 0 ? 'button' : _b, className = _a.className, size = _a.size, view = _a.view, rest = __rest(_a, ["Element", "className", "size", "view"]);
    return (jsx("div", { className: classNames('icon-button', {
            'icon-button_small': size === 'small',
            'icon-button_medium': size === 'medium',
            'icon-button_small_default': view === 'default',
            'icon-button_small_contrast': view === 'contrast',
        }), children: jsx(Element, __assign({ className: classNames(className, 'icon-button-element') }, rest)) }));
};

var COLORS = {
    default: '#000',
    contrast: '#fff',
};

var css_248z$1 = ".ci-icon {\n  overflow: hidden;\n  stroke-width: 2;\n  stroke-linecap: round;\n  stroke-linejoin: round;\n}\n";
styleInject(css_248z$1);

var CloseIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { stroke: COLORS[view], className: "ci-icon", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M18 6L6 18M6 6L18 18" }) }));
};

var DownloadIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { width: 24, height: 24, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsxs("g", { children: [jsx("path", { d: "M17.446 3.13411C16.1399 2.88803 14.3925 2.8 12 2.8C9.60754 2.8 7.86012 2.88803 6.55397 3.13411C5.26535 3.3769 4.58469 3.74215 4.16342 4.16342C3.74215 4.58469 3.3769 5.26535 3.13411 6.55397C2.88803 7.86012 2.8 9.60754 2.8 12L1 12C1 2.375 2.375 1 12 1C21.625 1 23 2.375 23 12L21.2 12C21.2 9.60754 21.112 7.86012 20.8659 6.55397C20.6231 5.26535 20.2579 4.58469 19.8366 4.16342C19.4153 3.74215 18.7347 3.3769 17.446 3.13411Z", fill: COLORS[view] }), jsx("path", { d: "M6.36362 16.3635L12 22L17.6364 16.3636L16.3636 15.0908L12.9 18.5544L12.9 8.72719L11.1 8.72719L11.1 18.5544L7.63642 15.0907L6.36362 16.3635Z", fill: COLORS[view] })] }) }));
};

var useCloseHandlers = function (ref, close) {
    useEffect(function () {
        var handleKeyDown = function (e) {
            if (e.key === 'Escape') {
                close();
            }
        };
        // при клике на картинку в чатике фокус переходит внутрь айфрейма
        // нужно вернуть фокус на вьевер снаружи, чтобы работал обработчик keydown
        if (ref.current) {
            ref.current.tabIndex = -1;
            ref.current.focus();
        }
        window.addEventListener('keydown', handleKeyDown);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [close, ref]);
    useEffect(function () {
        var handleClick = function (e) {
            if (e.target === ref.current) {
                close();
            }
        };
        window.addEventListener('click', handleClick);
        return function () {
            window.removeEventListener('click', handleClick);
        };
    }, [ref, close]);
};

var DESKTOP_PADDINGS = 86;
var useImageDimensions = function (containerRef, originalWidth, originalHeight) {
    var breakpoint = useBreakpoint();
    var isMobile = breakpoint === Breakpoint.XS || breakpoint === Breakpoint.S;
    var _a = useState({
        maxWidth: 0,
        maxHeight: 0,
    }), maxDimensions = _a[0], setMaxDimensions = _a[1];
    useEffect(function () {
        var container = containerRef.current;
        if (!container) {
            return undefined;
        }
        var aspectRatio = originalWidth / originalHeight;
        var observer = new ResizeObserver(function (entries) {
            var entry = entries[0];
            var containerWidth = entry.contentRect.width;
            var containerHeight = entry.contentRect.height;
            var calculatedMaxWidth = Math.min(
            // 2 горизонтальных паддинга на десктопе
            containerWidth - (isMobile ? 0 : DESKTOP_PADDINGS * 2), containerHeight * aspectRatio);
            var calculatedMaxHeight = Math.min(
            // 2 вертикальных паддинга на десктопе
            containerHeight - (isMobile ? 0 : DESKTOP_PADDINGS * 2), containerWidth / aspectRatio);
            setMaxDimensions({ maxWidth: calculatedMaxWidth, maxHeight: calculatedMaxHeight });
        });
        observer.observe(container);
        return function () { return observer.disconnect(); };
    }, [containerRef, originalHeight, originalWidth, isMobile]);
    return maxDimensions;
};

var downloadFile = function (url) {
    var a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

var css_248z = ".viewer-overlay {\n  position: fixed;\n  inset: 0 0;\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  background: #000000E5;\n  z-index: 2000;\n}\n.viewer-header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 19px 24px 8px 19px;\n  width: 100%;\n  box-sizing: border-box;\n  height: 82px;\n  z-index: 1;\n}\n@media (max-width: 1019px) {\n  .viewer-header {\n    justify-content: flex-end;\n    height: 64px;\n    padding: 8px 16px;\n    background: #000000e6;\n  }\n}\n.viewer-content {\n  display: flex;\n  flex-grow: 1;\n}\n.viewer-actions {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  column-gap: 12px;\n}\n.viewer-caption {\n  flex: 1;\n  overflow: hidden;\n  color: #FFF;\n  font-family: Arial, sans-serif;\n  font-style: normal;\n  font-weight: 400;\n  line-height: 22px;\n}\n.viewer-footer .viewer-caption {\n  padding-left: 48px;\n  text-align: center;\n}\n.viewer-title {\n  font-size: 16px;\n  line-height: 22px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n@media (max-width: 1019px) {\n  .viewer-title {\n    font-size: 12px;\n    line-height: 18px;\n  }\n}\n.viewer-subtitle {\n  font-size: 14px;\n  line-height: 20px;\n}\n@media (max-width: 1019px) {\n  .viewer-subtitle {\n    font-size: 10px;\n    line-height: 14px;\n  }\n}\n.viewer-image {\n  display: block;\n  object-fit: cover;\n  overflow: hidden;\n  max-width: 100%;\n}\n.viewer-image-hidden {\n  opacity: 0;\n}\n.viewer-loader,\n.viewer-image {\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n.viewer-loader svg {\n  height: 64px;\n  width: 64px;\n}\n@media (max-width: 1019px) {\n  .viewer-loader svg {\n    height: 48px;\n    width: 48px;\n  }\n}\n.viewer-footer {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  background: #000000e6;\n  padding: 12px 16px;\n  z-index: 1;\n}\n@media (min-width: 1020px) {\n  .viewer-footer {\n    display: none;\n  }\n}\n";
styleInject(css_248z);

var POLLING_INTERVAL = 3000;
var MAX_ATTEMPTS = 5;
var Viewer = memo(function (_a) {
    var title = _a.title, imageUrl = _a.imageUrl, imageOriginalWidth = _a.imageOriginalWidth, imageOriginalHeight = _a.imageOriginalHeight, subtitle = _a.subtitle, onClose = _a.onClose;
    var _b = useState(true), loading = _b[0], setLoading = _b[1];
    var _c = useState(0), loadAttempt = _c[0], setLoadAttempt = _c[1];
    var timerRef = useRef();
    var contentRef = useRef(null);
    var bodyRef = useRef(null);
    var dimensions = useImageDimensions(contentRef, imageOriginalWidth, imageOriginalHeight);
    var breakpoint = useBreakpoint();
    var isMobile = breakpoint === Breakpoint.XS || breakpoint === Breakpoint.S;
    useEffect(function () { return function () {
        // @ts-expect-error
        clearTimeout(timerRef.current);
    }; }, []);
    useCloseHandlers(bodyRef, onClose);
    var handleError = function () {
        if (loadAttempt >= MAX_ATTEMPTS) {
            setLoading(false);
            onClose();
            // TODO: send event to display error snackbar
            return;
        }
        timerRef.current = setTimeout(function () {
            setLoadAttempt(function (attempt) { return attempt + 1; });
        }, POLLING_INTERVAL);
    };
    var handleLoad = function () {
        setLoading(false);
    };
    var imageCaption = title || subtitle ? (jsxs("div", { className: "viewer-caption", children: [title && jsx("div", { className: "viewer-title", children: title }), subtitle && jsx("div", { className: "viewer-subtitle", children: subtitle })] })) : null;
    var closeButton = (jsx(IconButton, { size: "medium", view: "contrast", onClick: onClose, children: jsx(CloseIcon, { view: "contrast" }) }));
    var downloadButton = (jsx(IconButton, { size: "medium", view: "contrast", onClick: function () { return downloadFile(imageUrl); }, children: jsx(DownloadIcon, { view: "contrast" }) }));
    return ReactDOM.createPortal(jsxs("div", { ref: contentRef, className: "viewer-overlay", id: VIEWER_NODE_ID, children: [jsxs("div", { className: "viewer-header", children: [!isMobile && imageCaption, jsxs("div", { className: "viewer-actions", children: [!isMobile && downloadButton, closeButton] })] }), jsxs("div", { ref: bodyRef, className: "viewer-content", children: [jsx("img", { "data-qa": "file-image-preview", className: classNames('viewer-image', { 'viewer-image-hidden': loading }), src: imageUrl, style: __assign({}, dimensions), onError: handleError, onLoad: handleLoad }, loadAttempt), loading && (jsx("div", { className: "viewer-loader", children: jsx(Loading, { initial: LoadingColor.White, scale: LoadingScale.Medium }) }))] }), isMobile && (jsxs("div", { className: "viewer-footer", children: [imageCaption, downloadButton] }))] }), document.body);
});
Viewer.displayName = 'Viewer';

export { Viewer as V, styleInject as s };
//# sourceMappingURL=index-87699efb.js.map
